import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  TaskyBadge,
  TaskyButton,
  TaskyDialog,
  TaskyFormGroup,
  TaskyInput,
  TaskySelect,
  TaskySpinner,
} from '../tasky';
import type { TaskyBadgeProps } from '../tasky';
import {
  CreditWalletInfo,
  CreditTransaction,
  MonthlySummary,
  fetchOrganizationCredits,
  grantOrganizationCredits,
  updateOrganizationCreditSettings,
  fetchOrganizationCreditTransactions,
  fetchOrganizationCreditMonthlySummary,
} from '../../services/api/adminOrganizationService';
import { useToast } from '../../contexts/ToastContext';

interface CreditManagementSectionProps {
  organizationId: number;
  initialWallets?: CreditWalletInfo;
  initialTotalBalance?: number;
}

const WALLET_TYPE_LABELS: Record<string, string> = {
  monthly: '月間',
  free: '無料',
  additional: '追加',
};

const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  grant: '付与',
  consume: '消費',
  reset: 'リセット',
  expire: '期限切れ',
};

const WALLET_BADGE_VARIANTS: Record<string, TaskyBadgeProps['variant']> = {
  monthly: 'info',
  free: 'success',
  additional: 'warning',
};

const TRANSACTION_BADGE_VARIANTS: Record<string, TaskyBadgeProps['variant']> = {
  grant: 'success',
  consume: 'destructive',
  reset: 'secondary',
  expire: 'warning',
};

const CreditManagementSection: React.FC<CreditManagementSectionProps> = ({
  organizationId,
  initialWallets,
  initialTotalBalance,
}) => {
  const { showToast } = useToast();

  // Credit balance state
  const [wallets, setWallets] = useState<CreditWalletInfo | null>(initialWallets || null);
  const [totalBalance, setTotalBalance] = useState<number>(initialTotalBalance || 0);
  const [balanceLoading, setBalanceLoading] = useState(!initialWallets);

  // Settings state
  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [settingsData, setSettingsData] = useState<{
    monthly_credit_limit: number;
    daily_free_credits: number;
  }>({ monthly_credit_limit: 0, daily_free_credits: 0 });
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Grant modal state
  const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);
  const [grantAmount, setGrantAmount] = useState('');
  const [grantDescription, setGrantDescription] = useState('');
  const [grantSaving, setGrantSaving] = useState(false);

  // Transaction history state
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionLastPage, setTransactionLastPage] = useState(1);
  const [transactionTotal, setTransactionTotal] = useState(0);
  const [transactionFilter, setTransactionFilter] = useState<{
    wallet_type?: string;
    transaction_type?: string;
    year_month?: string;
  }>({});

  // Monthly summary state
  const [monthlySummary, setMonthlySummary] = useState<MonthlySummary[]>([]);
  const [monthlySummaryLoading, setMonthlySummaryLoading] = useState(false);

  const loadCredits = useCallback(async () => {
    setBalanceLoading(true);
    try {
      const data = await fetchOrganizationCredits(organizationId);
      setWallets(data.wallets);
      setTotalBalance(data.total_balance);
    } catch (e) {
      console.error('Failed to load credits:', e);
    } finally {
      setBalanceLoading(false);
    }
  }, [organizationId]);

  const loadTransactions = useCallback(async (page = 1) => {
    setTransactionsLoading(true);
    try {
      const data = await fetchOrganizationCreditTransactions(organizationId, {
        page,
        per_page: 15,
        ...(transactionFilter.wallet_type ? { wallet_type: transactionFilter.wallet_type } : {}),
        ...(transactionFilter.transaction_type ? { transaction_type: transactionFilter.transaction_type } : {}),
        ...(transactionFilter.year_month ? { year_month: transactionFilter.year_month } : {}),
      });
      setTransactions(data.data);
      setTransactionPage(data.current_page);
      setTransactionLastPage(data.last_page);
      setTransactionTotal(data.total);
    } catch (e) {
      console.error('Failed to load transactions:', e);
    } finally {
      setTransactionsLoading(false);
    }
  }, [organizationId, transactionFilter]);

  const loadMonthlySummary = useCallback(async () => {
    setMonthlySummaryLoading(true);
    try {
      const data = await fetchOrganizationCreditMonthlySummary(organizationId);
      setMonthlySummary(data);
    } catch (e) {
      console.error('Failed to load monthly summary:', e);
    } finally {
      setMonthlySummaryLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (!initialWallets) {
      loadCredits();
    }
  }, [loadCredits, initialWallets]);

  useEffect(() => {
    loadTransactions(1);
  }, [loadTransactions]);

  useEffect(() => {
    loadMonthlySummary();
  }, [loadMonthlySummary]);

  // Initialize settings from wallets
  useEffect(() => {
    if (wallets) {
      setSettingsData({
        monthly_credit_limit: wallets.monthly.limit,
        daily_free_credits: wallets.free.daily_amount,
      });
    }
  }, [wallets]);

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      await updateOrganizationCreditSettings(organizationId, settingsData);
      setIsEditingSettings(false);
      await loadCredits();
      showToast('クレジット設定を更新しました', 'success');
    } catch (e) {
      console.error('Failed to update settings:', e);
      showToast('クレジット設定の更新に失敗しました', 'error');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleGrantCredits = async () => {
    const amount = parseFloat(grantAmount);
    if (isNaN(amount) || amount <= 0) {
      showToast('有効な金額を入力してください', 'error');
      return;
    }
    if (!grantDescription.trim()) {
      showToast('説明を入力してください', 'error');
      return;
    }

    setGrantSaving(true);
    try {
      await grantOrganizationCredits(organizationId, {
        amount,
        description: grantDescription.trim(),
      });
      setIsGrantModalOpen(false);
      setGrantAmount('');
      setGrantDescription('');
      await loadCredits();
      await loadTransactions(1);
      showToast('追加クレジットを付与しました', 'success');
    } catch (e) {
      console.error('Failed to grant credits:', e);
      showToast('追加クレジットの付与に失敗しました', 'error');
    } finally {
      setGrantSaving(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCredits = (value: number): string => {
    return Math.abs(Math.round(value)).toLocaleString();
  };

  if (balanceLoading && !wallets) {
    return (
      <div className="flex items-center gap-3 justify-center py-8">
        <TaskySpinner size="md" />
        <span className="text-sm text-gray-600">クレジット情報を読み込み中...</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Credit Balances */}
      <div className="bg-white rounded-tasky-md p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-dark">クレジット残高</h3>
          <div className="flex items-center gap-2">
            <TaskyButton variant="outline" size="sm" onClick={() => loadCredits()}>更新</TaskyButton>
            <TaskyButton size="sm" onClick={() => setIsGrantModalOpen(true)}>追加クレジット付与</TaskyButton>
          </div>
        </div>

        <div className="text-sm text-gray-500 mb-4">
          合計残高: <strong className="text-lg font-semibold text-dark mx-1">{formatCredits(totalBalance)}</strong> クレジット
        </div>

        {wallets && (
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-warm-surface rounded-tasky-md p-4">
              <p className="text-xs text-gray-500 mb-2 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-primary" />
                月間クレジット
              </p>
              <p className="text-2xl font-semibold text-dark">
                {formatCredits(wallets.monthly.balance)}
              </p>
              <p className="text-[11px] text-gray-400 mt-1.5">
                上限: {wallets.monthly.limit.toLocaleString()}
              </p>
              <p className="text-[11px] text-gray-400">
                有効期限: {formatDate(wallets.monthly.expires_at)}
              </p>
            </div>

            <div className="bg-warm-surface rounded-tasky-md p-4">
              <p className="text-xs text-gray-500 mb-2 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-success" />
                無料クレジット
              </p>
              <p className="text-2xl font-semibold text-dark">
                {formatCredits(wallets.free.balance)}
              </p>
              <p className="text-[11px] text-gray-400 mt-1.5">
                日次付与: {wallets.free.daily_amount.toLocaleString()}
              </p>
              <p className="text-[11px] text-gray-400">
                有効期限: {formatDate(wallets.free.expires_at)}
              </p>
            </div>

            <div className="bg-warm-surface rounded-tasky-md p-4">
              <p className="text-xs text-gray-500 mb-2 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-purple" />
                追加クレジット
              </p>
              <p className="text-2xl font-semibold text-dark">
                {formatCredits(wallets.additional.balance)}
              </p>
              <p className="text-[11px] text-gray-400 mt-1.5">
                有効期限: なし（永続）
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Credit Settings */}
      <div className="bg-white rounded-tasky-md p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-dark">クレジット設定</h3>
          <div className="flex items-center gap-2">
            {!isEditingSettings ? (
              <TaskyButton variant="outline" size="sm" onClick={() => setIsEditingSettings(true)}>編集</TaskyButton>
            ) : (
              <>
                <TaskyButton variant="outline" size="sm" onClick={() => setIsEditingSettings(false)}>キャンセル</TaskyButton>
                <TaskyButton size="sm" onClick={handleSaveSettings} disabled={settingsSaving}>
                  {settingsSaving ? '保存中...' : '保存'}
                </TaskyButton>
              </>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 w-40">月間クレジット上限</span>
            {!isEditingSettings ? (
              <span className="text-sm text-dark">{settingsData.monthly_credit_limit.toLocaleString()}</span>
            ) : (
              <TaskyInput
                type="number"
                value={settingsData.monthly_credit_limit}
                onChange={(e) => setSettingsData({ ...settingsData, monthly_credit_limit: parseInt(e.target.value) || 0 })}
                min="0"
                className="w-44 h-9"
              />
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 w-40">日次無料クレジット</span>
            {!isEditingSettings ? (
              <span className="text-sm text-dark">{settingsData.daily_free_credits.toLocaleString()}</span>
            ) : (
              <TaskyInput
                type="number"
                value={settingsData.daily_free_credits}
                onChange={(e) => setSettingsData({ ...settingsData, daily_free_credits: parseFloat(e.target.value) || 0 })}
                min="0"
                step="0.1"
                className="w-44 h-9"
              />
            )}
          </div>
        </div>
      </div>

      {/* Monthly Summary Chart */}
      <div className="bg-white rounded-tasky-md p-5">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-dark">月別クレジット推移（過去12ヶ月）</h3>
        </div>

        {monthlySummaryLoading ? (
          <div className="flex items-center gap-3 justify-center py-8">
            <TaskySpinner size="sm" />
            <span className="text-sm text-gray-600">読み込み中...</span>
          </div>
        ) : monthlySummary.length > 0 ? (
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={monthlySummary} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tickFormatter={(v: string) => v.substring(5)} />
                <YAxis />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    Math.round(value).toLocaleString(),
                    name === 'consumed' ? '消費' : '付与',
                  ]}
                  labelFormatter={(label: string) => `${label}月`}
                />
                <Legend formatter={(value: string) => (value === 'consumed' ? '消費' : '付与')} />
                <Bar dataKey="granted" fill="#10B981" name="granted" />
                <Bar dataKey="consumed" fill="#EF4444" name="consumed" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-8">データがありません</p>
        )}
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-tasky-md p-5">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-dark">取引履歴 ({transactionTotal}件)</h3>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="w-40">
            <TaskySelect
              value={transactionFilter.wallet_type || ''}
              onChange={(e) => setTransactionFilter({ ...transactionFilter, wallet_type: e.target.value || undefined })}
              className="h-9 text-sm"
            >
              <option value="">全ウォレット</option>
              <option value="monthly">月間</option>
              <option value="free">無料</option>
              <option value="additional">追加</option>
            </TaskySelect>
          </div>
          <div className="w-40">
            <TaskySelect
              value={transactionFilter.transaction_type || ''}
              onChange={(e) => setTransactionFilter({ ...transactionFilter, transaction_type: e.target.value || undefined })}
              className="h-9 text-sm"
            >
              <option value="">全種別</option>
              <option value="grant">付与</option>
              <option value="consume">消費</option>
              <option value="reset">リセット</option>
              <option value="expire">期限切れ</option>
            </TaskySelect>
          </div>
          <input
            type="month"
            value={transactionFilter.year_month || ''}
            onChange={(e) => setTransactionFilter({ ...transactionFilter, year_month: e.target.value || undefined })}
            placeholder="年月を選択"
            className="h-9 px-3 text-sm text-dark bg-white border border-warm-border rounded-tasky-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
          {transactionFilter.year_month && (
            <TaskyButton
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setTransactionFilter({ ...transactionFilter, year_month: undefined })}
            >
              年月クリア
            </TaskyButton>
          )}
        </div>

        {transactionsLoading ? (
          <div className="flex items-center gap-3 justify-center py-8">
            <TaskySpinner size="sm" />
            <span className="text-sm text-gray-600">読み込み中...</span>
          </div>
        ) : transactions.length > 0 ? (
          <>
            <div className="border border-warm-border rounded-tasky-md overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="bg-warm-surface">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">日時</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ウォレット</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">種別</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">金額</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">残高</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ユーザー</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">説明</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-warm-border">
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-warm-bg transition-colors">
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(tx.created_at)}</td>
                      <td className="px-4 py-3">
                        <TaskyBadge variant={WALLET_BADGE_VARIANTS[tx.wallet_type] || 'secondary'}>
                          {WALLET_TYPE_LABELS[tx.wallet_type] || tx.wallet_type}
                        </TaskyBadge>
                      </td>
                      <td className="px-4 py-3">
                        <TaskyBadge variant={TRANSACTION_BADGE_VARIANTS[tx.transaction_type] || 'secondary'}>
                          {TRANSACTION_TYPE_LABELS[tx.transaction_type] || tx.transaction_type}
                        </TaskyBadge>
                      </td>
                      <td className={`px-4 py-3 font-medium ${tx.transaction_type === 'consume' ? 'text-destructive' : 'text-success'}`}>
                        {tx.amount < 0 ? '-' : '+'}{formatCredits(tx.amount)}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{formatCredits(tx.balance_after)}</td>
                      <td className="px-4 py-3 text-gray-700">{tx.user?.name || '-'}</td>
                      <td className="px-4 py-3 text-gray-500 max-w-[240px] truncate" title={tx.description || undefined}>{tx.description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {transactionLastPage > 1 && (
              <div className="flex items-center justify-center gap-3 mt-4">
                <TaskyButton
                  variant="outline"
                  size="sm"
                  onClick={() => loadTransactions(transactionPage - 1)}
                  disabled={transactionPage <= 1}
                >
                  前へ
                </TaskyButton>
                <span className="text-sm text-gray-500">{transactionPage} / {transactionLastPage}</span>
                <TaskyButton
                  variant="outline"
                  size="sm"
                  onClick={() => loadTransactions(transactionPage + 1)}
                  disabled={transactionPage >= transactionLastPage}
                >
                  次へ
                </TaskyButton>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-400 text-center py-8">取引履歴がありません</p>
        )}
      </div>

      {/* Grant Credits Modal */}
      <TaskyDialog
        open={isGrantModalOpen}
        onClose={() => setIsGrantModalOpen(false)}
        title="追加クレジット付与"
        footer={
          <>
            <TaskyButton variant="outline" size="sm" onClick={() => setIsGrantModalOpen(false)}>
              キャンセル
            </TaskyButton>
            <TaskyButton size="sm" onClick={handleGrantCredits} disabled={grantSaving}>
              {grantSaving ? '処理中...' : '付与する'}
            </TaskyButton>
          </>
        }
      >
        <div className="space-y-4">
          <TaskyFormGroup label="付与額">
            <TaskyInput
              type="number"
              value={grantAmount}
              onChange={(e) => setGrantAmount(e.target.value)}
              placeholder="例: 1000"
              min="0.0001"
              step="1"
            />
          </TaskyFormGroup>
          <TaskyFormGroup label="説明">
            <TaskyInput
              type="text"
              value={grantDescription}
              onChange={(e) => setGrantDescription(e.target.value)}
              placeholder="例: 追加購入分"
              maxLength={255}
            />
          </TaskyFormGroup>
        </div>
      </TaskyDialog>
    </div>
  );
};

export default CreditManagementSection;
